import { useEffect, useRef, EffectCallback, DependencyList, useState } from 'react';
import deepEqual from 'deep-equal';

// The following hooks expose the deep-equal library into React hooks.  This allows for deep comparison of objects
// in the dependency arrays.  This prevents unnecessary re-renders when the reference of an object changes, but the
// contents of the object do not.  Because deep-equal can be a heavy operation (especially on large, nested objects),
// it is recommended to use these hooks sparingly.

// useDeepCompareMemoize has the same signature useMemo, but with deep comparison built in.  It will only return a new
// value if the contents of the object have changed (not if the reference has changed).
export const useDeepCompareMemoize = <T>(value: T) => {
  const ref = useRef<T>();
  if (!deepEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
};

// useDeepCompareEffect is like useEffect, but with deep comparison built in.  It will only run the effect if the
// dependencies values have changed (not if the dependencies reference has changed).
export const useDeepCompareEffect = (callback: EffectCallback, dependencies: DependencyList) => {
  useEffect(callback, dependencies.map(useDeepCompareMemoize));
};

// useDeepCompareState is like useState, but with deep comparison built in.  It will only update the state if the value
// has changed (not if the reference has changed).
export const useDeepCompareState = <T>(initialState: T): [T, (value: T) => void] => {
  const ref = useRef<T>();
  ref.current = initialState;
  const [state, setState] = useState(initialState);
  const set = (value: T) => {
    if (!deepEqual(value, ref.current)) {
      ref.current = value;
      setState(value);
    }
  };

  return [state, set];
};
